import { useState, useContext } from "react"
import Logger from "classes/Logger"
import useCancelToken from "hooks/UseCancelToken"
import { getItemsQuantityFull } from "services/ItemsService"
import { AxiosResponse } from "axios"
import { IResponseService, ICollection } from "interfaces/IResponseService"
import { IItems } from "interfaces/IItemsOrder"
import { IShippinGroupDetail } from "interfaces/IOrder"
import {
  attachFileShippingGroup,
  executeShipingGroupAction,
  updateShippingGroup,
  updateShippingGroupTrace,
} from "services/ShippingGroupService"
import { EShippinggroupAction } from "enums/shippinggroupEmun"
import { GlobalContext } from "context/context"
import { NOTIFY_PACKING } from "helpers/constHelper"
import { useHistory } from "react-router-dom"
import { TransactionCodeEnum } from "enums/TransactionCodeEnum"
import useLogin from "hooks/UseLogin"

interface UseReversaOrderReturn {
  getItemsSeriados: (
    skus: Array<string>,
    channel: string,
  ) => Promise<AxiosResponse<IResponseService<ICollection<IItems>>>>
  loadingSendData: boolean
  loadingInstallation: boolean
  setLoadingInstallation: (loading: boolean) => void
  handleClickSendData: (
    channel: string,
    itemsSeriados: any,
    itemsNoSeriados: any,
    shippinGroupDetail: IShippinGroupDetail,
  ) => void
  attachFile: (shippingGroupId: string, filesShippinGroup: any) => Promise<any>
  updateTraceShippingGroupWithFiles: (shippingGroupId: string) => Promise<void>
}

const useReversaOrder = (): UseReversaOrderReturn => {
  const history = useHistory()
  const { errorHander, notification } = useContext(GlobalContext)
  const { logout } = useLogin()

  const { newCancelToken, isCancel } = useCancelToken()
  const [loadingSendData, setLoadingSendData] = useState<boolean>(false)
  const [loadingInstallation, setLoadingInstallation] = useState<boolean>(false)

  // Función helper para manejar errores 401
  const handleUnauthorizedError = (error: any) => {
    if (error.response?.status === TransactionCodeEnum.unauthorized) {
      Logger.error("Error 401 Unauthorized detectado:", error)
      logout()
      return true
    }
    return false
  }

  const getItemsSeriados = async (
    skus: Array<string>,
    channel?: string,
  ): Promise<AxiosResponse<IResponseService<ICollection<IItems>>>> => {
    const body = {
      channel: channel,
      skipRecords: 0,
      maxRecords: 1000,
      project: ["sku", "custom.seriado"],
      filter: {
        sku: skus,
        "custom.seriado": "S",
      },
    }

    try {
      const response = await getItemsQuantityFull(body, newCancelToken())

      return response
    } catch (error: any) {
      Logger.error("Error al obtener cantidad de items por fuente:", error)
      if (isCancel(error)) {
        throw error
      }
      if (handleUnauthorizedError(error)) {
        throw error
      }
      throw error
    }
  }

  const verificarCtoStatus = (itemsSeriados: any[], itemsNoSeriados: any[]): string => {
    const todosLosItems = [...itemsSeriados, ...itemsNoSeriados]

    if (todosLosItems.length === 0) {
      return "Incompleta"
    }

    const todosCompletos = todosLosItems.every((item) => {
      const quantity = item.quantity || 0
      const quantityReceived = item.custom?.quantityReceived || 0

      return quantity === quantityReceived
    })

    return todosCompletos ? "Completa" : "Incompleta"
  }

  const procesarItemsData = (itemsSeriados: any[], itemsNoSeriados: any[]) => {
    const allItems = [...itemsSeriados, ...itemsNoSeriados]

    return allItems.map((item) => ({
      sku: item.sku,
      custom: {
        quantityReceived: item.custom?.quantityReceived || 0,
        seriesReceived: item.custom?.seriesReceived === "" ? "0" : item.custom?.seriesReceived,
      },
    }))
  }

  const updateShippingGroupCustomField = async (
    shippingGroupId: string,
    itemsSeriados: any,
    itemsNoSeriados: any,
    statusReversa: string,
  ): Promise<boolean> => {
    try {
      // const ctoStatus = verificarCtoStatus(itemsSeriados, itemsNoSeriados) // No longer needed here as passed in
      const allItems = procesarItemsData(itemsSeriados, itemsNoSeriados)

      const response = await updateShippingGroup(
        {
          shippingGroupId,
          custom: {
            infoReversa: {
              status: statusReversa,
            },
          },
          items: allItems,
        },
        newCancelToken(),
      )

      Logger.info("updateShippingGroupCustomField ejecutada exitosamente", response?.data?.message)
      return true
    } catch (error: any) {
      Logger.error("Error en updateShippingGroupCustomField:", error)
      if (isCancel(error)) {
        throw error
      }
      if (handleUnauthorizedError(error)) {
        throw error
      }
      return false
    }
  }

  const executeActionShippingGroup = async (
    shippingGroupId: string,
    action: EShippinggroupAction,
    requestBody?: any,
  ): Promise<boolean> => {
    try {
      const response = await executeShipingGroupAction({
        shippingGroupId,
        action: action,
        requestBody,
      })

      Logger.info("executeActionShippingGroup ejecutada exitosamente", response?.data?.message)
      return true
    } catch (error: any) {
      Logger.error("Error en executeActionShippingGroup:", error)
      if (isCancel(error)) {
        throw error
      }
      if (handleUnauthorizedError(error)) {
        throw error
      }
      return false
    }
  }

  const handleClickSendData = async (
    channel: string,
    itemsSeriados: any,
    itemsNoSeriados: any,
    shippinGroupDetail: IShippinGroupDetail,
  ) => {
    try {
      setLoadingSendData(true)

      const ctoStatus = verificarCtoStatus(itemsSeriados, itemsNoSeriados)

      // Determine status for Reversa based on completion
      let statusReversa = ""
      if (ctoStatus === "Completa") {
        statusReversa = "Pendiente de Retiro"
      } else {
        statusReversa = "Parcial"
      }

      Logger.info(`Iniciando flujo Reversa (${ctoStatus}) - Paso 1: updateShippingGroupCustomField`)
      const resultadostep1 = await updateShippingGroupCustomField(
        shippinGroupDetail.id,
        itemsSeriados,
        itemsNoSeriados,
        statusReversa,
      )

      if (!resultadostep1) {
        notification?.dispatch({
          type: "ADD_NOTIFICATION",
          payload: {
            message: "Ha ocurrido un error en la tranferencia de productos. por favor intente nuevamente",
            title: "ERROR",
            type: "danger",
            delay: 6000,
          },
        })
        Logger.error("Flujo interrumpido: updateShippingGroupCustomField falló")
        return
      }

      // If Partial, stop here ("no se ejecutaria el executeActionShippingGroup")
      if (ctoStatus !== "Completa") {
        Logger.info("Reversa Parcial: Finalizando flujo sin ejecutar acciones")
        setLoadingSendData(false)
        notification?.dispatch({
          type: "ADD_NOTIFICATION",
          payload: {
            message: "Reversa parcial guardada correctamente",
            title: "Reversa Parcial",
            type: "success",
            delay: 6000,
          },
        })
        setTimeout(() => history.push("/order/pending_reversa"), 3000)
        return
      }

      // If Complete, execute actions (omit inventory and trace)
      Logger.info("Reversa Completa: Ejecutando acciones (accept -> packed)")

      const allItems = [...itemsSeriados, ...itemsNoSeriados]
      const requestBodyAccept = {
        seller: {
          id: "1",
        },
        items: allItems.map((item) => ({
          sku: item.sku,
          quantity: item.quantity,
          custom: item.custom,
        })),
      }

      // Execute 'accept'
      const action1 = await executeActionShippingGroup(
        shippinGroupDetail.id,
        EShippinggroupAction.accept,
        requestBodyAccept,
      )
      if (!action1) {
        Logger.error("Flujo interrumpido: executeActionShippingGroup (accept) falló")
        throw new Error("Action accept failed")
      }

      // Execute 'packed'
      const action2 = await executeActionShippingGroup(shippinGroupDetail.id, EShippinggroupAction.packed)
      if (!action2) {
        Logger.error("Flujo interrumpido: executeActionShippingGroup (packed) falló")
        throw new Error("Action packed failed")
      }

      setLoadingSendData(false)

      notification?.dispatch({
        type: "ADD_NOTIFICATION",
        payload: {
          message: "Productos transferidos correctamente",
          title: NOTIFY_PACKING,
          type: "success",
          delay: 6000,
        },
      })

      setTimeout(() => history.push("/dashboard"), 3000)
      return
    } catch (error: any) {
      setLoadingSendData(false)
      if (isCancel(error)) {
        throw error
      }
      if (handleUnauthorizedError(error)) {
        throw error
      }

      Logger.error("Error en flujo secuencial:", error)
      errorHander?.dispatch({ hasError: true, code: error.response?.status })

      notification?.dispatch({
        type: "ADD_NOTIFICATION",
        payload: {
          message: "Ha ocurrido un error en el proceso.",
          title: "ERROR",
          type: "danger",
          delay: 6000,
        },
      })
    } finally {
      setLoadingSendData(false)
    }
  }

  const attachFile = async (shippingGroupId: string, filesShippinGroup: any) => {
    try {
      const formData = new FormData()
      const files = filesShippinGroup.files || filesShippinGroup

      if (files && files.length > 0) {
        for (let i = 0; i < files.length; i++) {
          formData.append("files", files[i])
        }
      }

      const response = await attachFileShippingGroup(shippingGroupId, formData)
      return response
    } catch (error: any) {
      if (isCancel(error)) {
        throw error
      }
      errorHander?.dispatch({ hasError: true, code: error.response?.status })
      return false
    }
  }

  const updateTraceShippingGroupWithFiles = async (shippingGroupId: string): Promise<void> => {
    try {
      const infoTrace = "Adjuntando archivos"
      const body = {
        shippingGroupId,
        custom: {
          name: infoTrace,
          description: infoTrace,
        },
      }

      const response = await updateShippingGroupTrace(body, newCancelToken())
      Logger.info("updateTraceShippingGroupWithFiles ejecutada exitosamente", response?.data?.message)
    } catch (error: any) {
      Logger.error("Error en updateTraceShippingGroupWithFiles:", error)
      if (isCancel(error)) {
        throw error
      }
      throw error // Re-throw para manejarlo en el componente si es necesario
    }
  }

  return {
    getItemsSeriados,
    loadingSendData,
    handleClickSendData,
    loadingInstallation,
    setLoadingInstallation,
    attachFile,
    updateTraceShippingGroupWithFiles,
  }
}

export default useReversaOrder
